/**
 * Chunk-load recovery.
 *
 * When a new version is deployed, the content-hashed JS chunks referenced by
 * an already-open (stale) tab no longer exist on the server. Any subsequent
 * lazy `import()` — e.g. navigating to a Custom Module route or a lazily
 * imported firebase call — then fails with a "ChunkLoadError" /
 * "Loading chunk failed" / "Failed to fetch dynamically imported module".
 *
 * The fix is simply to reload the page once: index.html is served no-cache, so
 * the reload pulls the new hashed chunk filenames and the import succeeds.
 *
 * A sessionStorage guard prevents an infinite reload loop in the (rare) case
 * where the chunk genuinely can't be fetched even after a fresh load.
 */

const RELOAD_GUARD_KEY = 'chunkReloadAt';
const RELOAD_LOOP_WINDOW_MS = 15_000;

const CHUNK_ERROR_PATTERNS = [
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
];

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const err = error as { name?: string; message?: string };
  const name = err.name || '';
  const message = err.message || String(error);
  if (name === 'ChunkLoadError') return true;
  return CHUNK_ERROR_PATTERNS.some(p => message.includes(p));
}

/**
 * Reload the page to fetch the freshly deployed chunks, unless we just did so
 * (loop guard). Returns true if a reload was triggered.
 */
export function attemptChunkReload(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (last && Date.now() - last < RELOAD_LOOP_WINDOW_MS) {
      // We already reloaded very recently and still hit a chunk error — the
      // chunk is genuinely missing/broken. Stop reloading and let the error
      // surface so it gets reported instead of looping forever.
      return false;
    }
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode / blocked) — reload anyway once.
  }
  window.location.reload();
  return true;
}

/**
 * Global backstop for chunk-load failures that don't propagate through a React
 * error boundary — e.g. an `await import()` inside an event handler. Install
 * once at app startup.
 */
export function installGlobalChunkReloadHandler(): void {
  window.addEventListener('error', event => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
      attemptChunkReload();
    }
  });

  window.addEventListener('unhandledrejection', event => {
    if (isChunkLoadError(event.reason)) {
      attemptChunkReload();
    }
  });
}
